<template>
    <div>
        <PageHeader :title="isEdit ? 'Detalhes do Evento' : 'Novo Evento'">
            <template v-slot:icon>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="page-header-icon">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                </svg>
            </template>
        </PageHeader>

        <div class="flex justify-center mb-6" v-if="loading">
            <Loading text="Buscando dados do evento... Aguarde..." :show="loading" />
        </div>

        <!-- EVENT INFO (labels) + EDIT FORM -->
        <div class="bg-white rounded-lg shadow-sm p-4 mb-4" v-if="!loading">
            <div class="flex items-center justify-between mb-4">
                <div class="flex items-center gap-x-3">
                    <BackButton :to="{ name: 'eventos' }" />
                    <h2 class="text-lg font-semibold text-green-900">{{ editing ? (isEdit ? 'Editar evento' : 'Novo evento') : (event.theme || 'Evento') }}</h2>
                    <span v-if="event.is_deleted" class="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded font-medium">inativo</span>
                </div>
                <div class="flex gap-x-2" v-if="isEdit">
                    <BaseButton v-if="event.is_deleted" skin="primary-outline" :disabled="restoringEvent" @click="restoreEvent">Restaurar</BaseButton>
                    <template v-if="!event.is_deleted">
                        <BaseButton v-if="!editing" skin="primary-outline" @click="startEdit">Editar</BaseButton>
                        <BaseButton v-else skin="primary-outline" @click="cancelEdit">Cancelar</BaseButton>
                    </template>
                </div>
            </div>

            <!-- READ-ONLY -->
            <div v-if="!editing && isEdit" class="grid grid-cols-3 gap-x-8 gap-y-4">
                <div>
                    <span class="label">Tema</span>
                    <p class="text-sm text-green-900 font-semibold">{{ event.theme || '--' }}</p>
                </div>
                <div>
                    <span class="label">Instrutor</span>
                    <p class="text-sm text-green-900">{{ event.instructor || '--' }}</p>
                </div>
                <div>
                    <span class="label">Data e hora</span>
                    <p class="text-sm text-green-900">{{ event.event_date ? dateTime(event.event_date) : '--' }}</p>
                </div>
                <div>
                    <span class="label">Carga horária</span>
                    <p class="text-sm text-green-900">{{ event.workload_hours ? `${event.workload_hours} hora(s)` : '--' }}</p>
                </div>
                <div>
                    <span class="label">Tipo de treinamento</span>
                    <p class="text-sm text-green-900">{{ event.training_type?.name || '--' }}</p>
                </div>
                <div>
                    <span class="label">Formulário</span>
                    <p class="text-sm text-green-900">{{ event.form?.name || '--' }}</p>
                </div>
                <div>
                    <span class="label">Modelo de certificado</span>
                    <p class="text-sm text-green-900">{{ event.certificate_template?.name || '--' }}</p>
                </div>
                <div>
                    <span class="label">Emite certificado</span>
                    <Tag v-if="event.emits_certificate" type="success" text="Sim" />
                    <Tag v-else type="warning" text="Não" />
                </div>
            </div>

            <!-- EDIT FORM -->
            <form v-if="editing" @submit.prevent="submit" novalidate>
                <div class="grid grid-cols-3 gap-x-8 gap-y-4">
                    <div>
                        <BaseInput label="Tema" placeholder="Tema do treinamento" v-model="event.theme" required="true" />
                    </div>
                    <div>
                        <BaseInput label="Instrutor" placeholder="Nome do instrutor" v-model="event.instructor" />
                    </div>
                    <div class="flex flex-col">
                        <label class="label">Data e hora</label>
                        <Datepicker locale="pt-BR" format="dd/MM/yyyy HH:mm" v-model="event.event_date" autoApply />
                    </div>
                    <div>
                        <BaseInput type="number" label="Carga horária (horas)" placeholder="Ex: 8" v-model="event.workload_hours" />
                    </div>
                    <div>
                        <BaseSelect label="Tipo de treinamento" emptyOption="-- Selecione --" :options="trainingTypeOptions" v-model="event.training_type_id" @change="onSelectTrainingType" />
                    </div>
                    <div>
                        <BaseSelect label="Formulário do evento" emptyOption="-- Selecione --" :options="formOptions" v-model="event.form_id" />
                    </div>
                    <div>
                        <BaseSelect label="Modelo de certificado" emptyOption="-- Selecione --" :options="certificateTemplateOptions" v-model="event.certificate_template_id" />
                    </div>
                    <div class="flex items-end">
                        <div class="rounded border border-gray-200 px-4 py-3 w-full">
                            <BaseCheckbox id="emits-certificate" label="Emite certificado" v-model="event.emits_certificate" />
                        </div>
                    </div>
                </div>
                <div class="flex justify-end gap-x-2 mt-4">
                    <BaseButton v-if="isEdit" type="button" skin="primary-outline" @click="cancelEdit">Cancelar</BaseButton>
                    <BaseButton skin="primary" :is-submit="true" :disabled="loadingSubmit">
                        <svg v-if="loadingSubmit" class="animate-spin ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ isEdit ? 'Atualizar' : 'Salvar' }}
                    </BaseButton>
                </div>
            </form>
        </div>

        <!-- PUBLIC LINK (edit only) -->
        <div class="bg-white border border-green-200 rounded-lg shadow-sm p-4 mb-4" v-if="!loading && isEdit">
            <label class="text-sm font-bold uppercase tracking-wide text-green-800">Link público de inscrição</label>
            <div class="flex flex-wrap items-start gap-x-2 gap-y-2 mt-1">
                <span class="text-base md:text-lg font-bold text-green-900 break-all pt-2">{{ registrationLink }}</span>
                <div class="ml-2">
                    <BaseButton skin="primary-outline" @click="copyRegistrationLink">{{ copied ? 'Copiado!' : 'Copiar' }}</BaseButton>
                </div>
            </div>
        </div>

        <!-- METRICS (edit only) -->
        <div class="grid grid-cols-2 gap-4 mb-4" v-if="!loading && isEdit">
            <div class="bg-green-50 border border-green-100 rounded-lg p-4">
                <span class="text-xs font-semibold uppercase text-gray-500">Participantes</span>
                <div class="text-2xl font-bold text-green-700">{{ event.participant_count ?? 0 }}</div>
            </div>
            <div class="bg-green-50 border border-green-100 rounded-lg p-4">
                <span class="text-xs font-semibold uppercase text-gray-500">Certificados enviados</span>
                <div class="text-2xl font-bold text-green-700">{{ event.certificate_sent_count ?? 0 }}/{{ event.participant_count ?? 0 }}</div>
            </div>
        </div>

        <!-- PARTICIPANTS (edit only) -->
        <div class="bg-white rounded-lg shadow-sm p-4" v-if="!loading && isEdit">
            <h2 class="text-lg font-semibold text-green-900 mb-3">Participantes</h2>
            <div>
                <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <div class="flex-1 min-w-0">
                            <BaseInput placeholder="Buscar por nome ou email" v-model="participantSearch" @input="onParticipantSearchTyping" />
                        </div>
                        <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
                            <BaseButton skin="primary" @click="openAddParticipant()">
                                <template v-slot:icon>
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                </template>
                                Adicionar participante
                            </BaseButton>
                            <BaseButton skin="primary-outline" :disabled="loadingParticipants" @click="loadParticipants">
                                <svg v-if="loadingParticipants" class="animate-spin ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Atualizar
                            </BaseButton>
                            <BaseButton skin="primary-outline" :disabled="loadingInsights" @click="downloadInsightsPdf">
                                <svg v-if="loadingInsights" class="animate-spin mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                </svg>
                                {{ loadingInsights ? 'Gerando...' : 'Baixar Resumo' }}
                            </BaseButton>
                            <BaseButton skin="primary-outline" :disabled="loadingViewInsights" @click="viewInsights">
                                <svg v-if="loadingViewInsights" class="animate-spin mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                </svg>
                                {{ loadingViewInsights ? 'Gerando...' : 'Ver Resumo' }}
                            </BaseButton>
                        </div>
                    </div>

                    <!-- Loading Skeleton -->
                    <div v-if="loadingParticipants" class="mt-6">
                        <table class="min-w-full">
                            <thead>
                                <tr>
                                    <th>Participante</th>
                                    <th>Inscrito em</th>
                                    <th>Certificado</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in 4" :key="`participant-skeleton-${item}`" class="table-row">
                                    <td v-for="cell in 4" :key="`participant-skeleton-cell-${cell}`">
                                        <div class="h-3 rounded bg-gray-200 animate-pulse"></div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <Alert class="mt-4" text="Nenhum participante encontrado" :show="!participants.length && !loadingParticipants" />

                    <div class="mt-4" v-if="participants.length && !loadingParticipants">
                        <table class="min-w-full">
                            <thead>
                                <tr>
                                    <th>Participante</th>
                                    <th>Inscrito em</th>
                                    <th>Certificado</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="table-row transition-opacity duration-300" :class="{ 'opacity-30 pointer-events-none': deletingParticipant[participant.uuid], 'bg-red-50': participant.certificate_status === 'failed' }" v-for="participant in participants" :key="participant.id">
                                    <td>
                                        <span class="text-sm leading-5 text-green-900 font-semibold">{{ participant.name }}</span>
                                        <div class="text-xs text-gray-500">{{ participant.email }}</div>
                                    </td>
                                    <td>
                                        <span class="text-sm leading-5 text-green-900">{{ participant.created_at ? dateTime(participant.created_at) : ' -- ' }}</span>
                                        <div v-if="participant.created_at" class="text-xs text-gray-400">{{ relativeTime(participant.created_at) }}</div>
                                    </td>
                                    <td>
                                        <Tag v-if="participant.certificate_status === 'pending'" type="warning" text="pendente" />
                                        <Tag v-else-if="participant.certificate_status === 'generated'" type="info" text="gerado" />
                                        <Tag v-else-if="participant.certificate_status === 'sent'" type="success" text="enviado" />
                                        <Tag v-else-if="participant.certificate_status === 'failed'" type="danger" text="falhou" />
                                        <span v-else class="text-sm leading-5 text-gray-400">--</span>
                                        <div v-if="participant.certificate_status === 'pending' && participant.certificate_scheduled_at" class="text-xs text-gray-400 mt-1">
                                            {{ dateTime(participant.certificate_scheduled_at) }}
                                        </div>
                                        <div v-if="participant.certificate_status === 'pending' && participant.certificate_scheduled_at" class="text-xs text-green-600 mt-1">
                                            {{ relativeTime(participant.certificate_scheduled_at) }}
                                        </div>
                                        <div v-if="participant.certificate_status === 'sent' && participant.certificate_sent_at" class="text-xs text-gray-400 mt-1">{{ dateTime(participant.certificate_sent_at) }}</div>
                                        <div v-if="participant.certificate_status === 'sent' && participant.certificate_sent_at" class="text-xs text-gray-400 mt-1">{{ relativeTime(participant.certificate_sent_at) }}</div>
                                    </td>
                                    <td>
                                        <div class="flex gap-x-4 items-center">
                                            <span title="Baixar certificado" class="cursor-pointer text-green-600 hover:text-green-800" @click="downloadCert(participant)">
                                                <svg v-if="!certLoading[participant.uuid]" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                                </svg>
                                                <svg v-else class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                            </span>
                                            <span title="Reenviar certificado por email" class="cursor-pointer text-green-600 hover:text-green-800" @click="resendCert(participant)">
                                                <svg v-if="!resendLoading[participant.uuid]" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                                                </svg>
                                                <svg v-else class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                            </span>
                                            <span title="Editar participante" class="cursor-pointer text-green-600 hover:text-green-800" @click="openEditParticipant(participant)">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                                                </svg>
                                            </span>
                                            <span title="Excluir participante" class="cursor-pointer text-red-400 hover:text-red-600" @click="deleteParticipant(participant)">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                                                </svg>
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <Pagination :total="participantsTotal" :limit="participantLimit" @changePage="changeParticipantPage" />
                    </div>
                </div>
        </div>

        <!-- INSIGHTS MODAL -->
        <ModalV2 ref="insightsModalRef" :hide="true" title="Resumo do Evento" size="xl">
            <template v-slot:body>
                <div v-if="insightsData" class="overflow-y-auto" style="max-height: 75vh;">
                    <!-- Header -->
                    <div class="mb-4">
                        <h2 class="text-xl font-bold text-green-900">{{ insightsData.event.theme }}</h2>
                        <p class="text-sm text-gray-500">{{ insightsData.event.meta }}</p>
                    </div>

                    <!-- KPIs -->
                    <div class="grid grid-cols-2 gap-4 mb-6">
                        <div class="bg-green-50 border border-green-100 rounded-lg p-4 text-center">
                            <div class="text-xs font-semibold uppercase text-gray-500">Participantes</div>
                            <div class="text-3xl font-bold text-green-700">{{ insightsData.participantCount }}</div>
                        </div>
                        <div class="bg-green-50 border border-green-100 rounded-lg p-4 text-center">
                            <div class="text-xs font-semibold uppercase text-gray-500">Responderam</div>
                            <div class="text-3xl font-bold text-green-700">{{ insightsData.answeredCount }}</div>
                        </div>
                    </div>

                    <!-- Participants table -->
                    <div class="mb-6" v-if="insightsData.participantRows.length">
                        <h3 class="text-base font-bold text-green-900 mb-2">Lista de Participantes</h3>
                        <div class="mb-3">
                            <BaseInput placeholder="Filtrar por empresa..." v-model="insightsCompanyFilter" />
                        </div>
                        <div class="overflow-x-auto">
                            <table class="min-w-full text-sm">
                                <thead>
                                    <tr class="bg-green-50">
                                        <th v-for="col in insightsData.participantColumns" :key="col.key" class="text-left px-3 py-2 text-xs font-semibold text-green-800 uppercase border-b border-green-100">{{ col.label }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, i) in filteredParticipantRows" :key="i" class="border-b border-gray-100 hover:bg-gray-50">
                                        <td v-for="col in insightsData.participantColumns" :key="col.key" class="px-3 py-2" :class="col.key === 'company' && row.companyCrossRef ? 'text-green-600 font-semibold' : 'text-gray-700'">{{ row[col.key] || '--' }}</td>
                                    </tr>
                                    <tr v-if="!filteredParticipantRows.length">
                                        <td :colspan="insightsData.participantColumns.length" class="px-3 py-4 text-center text-sm text-gray-400">Nenhum participante encontrado para essa empresa</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Questions -->
                    <div v-if="filteredQuestions.length">
                        <h3 class="text-base font-bold text-green-900 mb-3">Respostas</h3>
                        <div v-for="(q, qi) in filteredQuestions" :key="qi" class="mb-5 bg-gray-50 rounded-lg p-4">
                            <div class="font-semibold text-green-900 mb-2">{{ q.label }}</div>
                            <!-- Distribution (multiple choice / scale) -->
                            <table v-if="q.distribution" class="w-full text-sm">
                                <tbody>
                                    <tr v-for="d in q.distribution" :key="d.label" class="border-b border-gray-200">
                                        <td class="py-1 pr-4 text-gray-700 w-1/2">{{ d.label }}</td>
                                        <td class="py-1 pr-4 text-gray-500">{{ d.count }} resp.</td>
                                        <td class="py-1">
                                            <div class="flex items-center gap-2">
                                                <div class="flex-1 bg-gray-200 rounded h-2">
                                                    <div class="bg-green-500 h-2 rounded" :style="{ width: d.pct + '%' }"></div>
                                                </div>
                                                <span class="text-xs text-gray-500 w-10 text-right">{{ d.pct }}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div v-if="q.responses?.length" class="mt-3 overflow-x-auto">
                                <table class="w-full text-sm">
                                    <tbody>
                                        <tr v-for="(response, ri) in q.responses" :key="ri" class="border-b border-gray-200">
                                            <td class="py-1 pr-4 align-top text-green-700 font-semibold w-1/3">{{ response.name }}</td>
                                            <td class="py-1 align-top text-gray-700">{{ response.answer }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:footer>
                <BaseButton skin="primary-outline" @click="closeInsightsModal">Fechar</BaseButton>
                <BaseButton skin="primary" :disabled="loadingInsights" @click="downloadInsightsPdf">Baixar PDF</BaseButton>
            </template>
        </ModalV2>

        <!-- ADD PARTICIPANT MODAL -->
        <ModalV2 ref="participantModalRef" :hide="true" :title="isParticipantEdit ? 'Editar participante' : 'Adicionar participante'" size="md">
            <template v-slot:body>
                <div class="grid grid-cols-1 gap-y-4">
                    <BaseInput label="Nome" maxlength="120" v-model="participantForm.name" required="true" />
                    <BaseInput label="Email" type="email" maxlength="120" v-model="participantForm.email" required="true" />
                    <BaseInput label="CPF" maxlength="14" placeholder="000.000.000-00" v-model="participantForm.cpf" v-maska="'###.###.###-##'" />
                    <BaseInput label="Empresa" maxlength="120" v-model="participantForm.company" />
                </div>
            </template>
            <template v-slot:footer>
                <BaseButton skin="primary-outline" @click="closeAddParticipant()">Fechar</BaseButton>
                <BaseButton skin="primary" :disabled="loadingAddParticipant" @click="submitParticipant">
                    <svg v-if="loadingAddParticipant" class="animate-spin ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Salvar
                </BaseButton>
            </template>
        </ModalV2>
    </div>
</template>

<script>

import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Swal from 'sweetalert';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { EventService, TrainingTypeService, CertificateTemplateService, FormService } from '../services/Entity';
import BackButton from '@/components/BackButton';
import Tag from '@/components/Tag';
import Alert from '@/components/Alert';
import ModalV2 from '@/components/ModalV2';
import Pagination from '@/components/Pagination';
import { dateTime } from '../filters/date';

export default {
    name: 'Evento',
    components: {
        BackButton,
        Tag,
        Alert,
        ModalV2,
        Pagination
    },
    setup() {
        const route = useRoute();
        const router = useRouter();
        const isEdit = ref(false);
        const loading = ref(false);
        const loadingSubmit = ref(false);
        const event = ref({
            theme: '',
            instructor: '',
            event_date: null,
            workload_hours: '',
            training_type_id: '',
            form_id: '',
            certificate_template_id: '',
            emits_certificate: false
        });

        const trainingTypes = ref([]);
        const trainingTypeOptions = ref([]);
        const certificateTemplateOptions = ref([]);
        const formOptions = ref([]);

        const participants = ref([]);
        const participantsTotal = ref(0);
        const participantPage = ref(1);
        const participantLimit = ref(15);
        const participantSearch = ref('');
        let participantTypingTimeout = null;
        const loadingParticipants = ref(false);
        const participantModalRef = ref();
        const isParticipantEdit = ref(false);
        const participantForm = ref({ uuid: '', name: '', email: '', cpf: '', company: '' });
        const loadingAddParticipant = ref(false);
        const copied = ref(false);
        const editing = ref(false);
        const loadingInsights = ref(false);
        const loadingViewInsights = ref(false);
        const insightsModalRef = ref();
        const insightsData = ref(null);
        const insightsCompanyFilter = ref('');

        const filteredParticipantRows = computed(() => {
            const rows = insightsData.value?.participantRows || [];
            const q = insightsCompanyFilter.value.trim().toLowerCase();
            if (!q) return rows;
            return rows.filter(row => (row.company || '').toLowerCase().includes(q));
        });

        const filteredQuestions = computed(() => {
            const questions = insightsData.value?.questions || [];
            const q = insightsCompanyFilter.value.trim().toLowerCase();
            if (!q) return questions;
            const allowedNames = new Set(filteredParticipantRows.value.map(r => r.name));
            return questions.map(question => {
                const filteredResponses = (question.responses || []).filter(r => allowedNames.has(r.name));

                let distribution = question.distribution;
                if (distribution) {
                    if (filteredResponses.length) {
                        const counts = {};
                        filteredResponses.forEach(r => { const v = String(r.answer).trim(); counts[v] = (counts[v] || 0) + 1; });
                        const total = filteredResponses.length;
                        distribution = distribution.map(d => {
                            const count = counts[String(d.label).trim()] || 0;
                            return { label: d.label, count, pct: +(count * 100 / total).toFixed(1) };
                        });
                    } else {
                        distribution = null;
                    }
                }

                return { ...question, responses: filteredResponses, distribution };
            }).filter(question => question.responses?.length || question.distribution);
        });
        const certLoading = ref({});
        const restoringEvent = ref(false);
        const resendLoading = ref({});
        const deletingParticipant = ref({});

        const registrationLink = computed(() => {
            const uuid = event.value.uuid || route.params.uuid;
            return `${window.location.origin}/inscricao/${uuid}`;
        });

        async function init() {
            loading.value = true;
            await Promise.all([loadTrainingTypes(), loadCertificateTemplates(), loadForms()]);

            if (route.params.uuid) {
                isEdit.value = true;
                editing.value = false;
                await loadEvent(route.params.uuid);
                await loadParticipants();
            } else {
                editing.value = true;
            }

            loading.value = false;
        }

        function startEdit() {
            editing.value = true;
        }

        async function cancelEdit() {
            editing.value = false;
            await loadEvent(route.params.uuid);
        }

        async function loadEvent(uuid) {
            try {
                const { data } = await EventService.getByUuid(uuid);
                event.value = {
                    ...data,
                    training_type_id: data.training_type_id ?? data.training_type?.id ?? '',
                    certificate_template_id: data.certificate_template_id ?? data.certificate_template?.id ?? '',
                    form_id: data.form_id ?? data.form?.id ?? ''
                };
            } catch (err) {
                console.log(err);
                Swal({
                    title: 'Ocorreu um erro ao obter dados do evento',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            }
        }

        async function loadTrainingTypes() {
            try {
                const { data: { items } } = await TrainingTypeService.list(1, 9999);
                trainingTypes.value = items;
                trainingTypeOptions.value = items.map(item => ({ title: item.name, value: item.id }));
            } catch (err) {
                console.log(err);
            }
        }

        async function loadCertificateTemplates() {
            try {
                const { data: { items } } = await CertificateTemplateService.list(1, 9999);
                certificateTemplateOptions.value = items.map(item => ({ title: item.name, value: item.id }));
            } catch (err) {
                console.log(err);
            }
        }

        async function loadForms() {
            try {
                const { data: { items } } = await FormService.list(1, 9999, { type: 'event' });
                const eventForms = (items || []).filter(item => (item.type || item.form_body?.type) === 'event');
                formOptions.value = eventForms.map(item => ({ title: item.name, value: item.id }));
            } catch (err) {
                console.log(err);
            }
        }

        function onSelectTrainingType() {
            const selected = trainingTypes.value.find(item => String(item.id) === String(event.value.training_type_id));
            if (selected) {
                event.value.emits_certificate = !!selected.generates_certificate;
                if (selected.certificate_template_id && !event.value.certificate_template_id) {
                    event.value.certificate_template_id = selected.certificate_template_id;
                }
            }
        }

        function buildPayload() {
            return {
                theme: event.value.theme,
                instructor: event.value.instructor,
                event_date: event.value.event_date,
                workload_hours: event.value.workload_hours,
                training_type_id: event.value.training_type_id || null,
                form_id: event.value.form_id || null,
                certificate_template_id: event.value.certificate_template_id || null,
                emits_certificate: !!event.value.emits_certificate
            };
        }

        async function submit() {
            if (!event.value.theme || !event.value.theme.trim()) {
                Swal({ title: 'Informe o tema do evento', icon: 'info' });
                return;
            }

            try {
                loadingSubmit.value = true;

                if (isEdit.value) {
                    await EventService.update(route.params.uuid, buildPayload());
                    await loadEvent(route.params.uuid);
                    editing.value = false;
                    Swal({ title: 'Atualizado!', icon: 'success' });
                } else {
                    const { data } = await EventService.create(buildPayload());
                    Swal({ title: 'Evento criado!', icon: 'success' });
                    router.push({ name: 'evento', params: { uuid: data.uuid } });
                }
            } catch (err) {
                console.log(err);
                Swal({
                    title: 'Ocorreu um erro ao salvar o evento',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            } finally {
                loadingSubmit.value = false;
            }
        }

        async function loadParticipants() {
            try {
                loadingParticipants.value = true;
                const { data: { items, total } } = await EventService.listParticipants(route.params.uuid, participantPage.value, participantLimit.value, participantSearch.value.trim() || undefined);
                participants.value = items;
                participantsTotal.value = total;
            } catch (err) {
                console.log(err);
                Swal({
                    title: 'Ocorreu um erro ao obter os participantes',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            } finally {
                loadingParticipants.value = false;
            }
        }

        function onParticipantSearchTyping() {
            participantPage.value = 1;
            if (participantTypingTimeout) clearTimeout(participantTypingTimeout);
            participantTypingTimeout = setTimeout(() => {
                loadParticipants();
                participantTypingTimeout = null;
            }, 400);
        }

        async function changeParticipantPage(currentPage) {
            participantPage.value = currentPage;
            await loadParticipants();
        }

        function openAddParticipant() {
            isParticipantEdit.value = false;
            participantForm.value = { uuid: '', name: '', email: '', cpf: '', company: '' };
            participantModalRef.value.setModalStatus('open');
        }

        function openEditParticipant(participant) {
            isParticipantEdit.value = true;
            participantForm.value = {
                uuid: participant.uuid,
                name: participant.name || '',
                email: participant.email || '',
                cpf: participant.cpf || '',
                company: participant.company || ''
            };
            participantModalRef.value.setModalStatus('open');
        }

        function closeAddParticipant() {
            participantModalRef.value.setModalStatus('close');
        }

        async function submitParticipant() {
            if (!participantForm.value.name?.trim() || !participantForm.value.email?.trim()) {
                Swal({ title: 'Preencha nome e email', icon: 'info' });
                return;
            }

            try {
                loadingAddParticipant.value = true;
                const payload = {
                    name: participantForm.value.name.trim(),
                    email: participantForm.value.email.trim(),
                    cpf: participantForm.value.cpf?.trim() || null,
                    company: participantForm.value.company?.trim() || null
                };

                if (isParticipantEdit.value) {
                    await EventService.updateParticipant(route.params.uuid, participantForm.value.uuid, payload);
                } else {
                    await EventService.addParticipant(route.params.uuid, payload);
                }

                closeAddParticipant();
                await loadParticipants();
                await loadEvent(route.params.uuid);
                Swal({ title: isParticipantEdit.value ? 'Participante atualizado!' : 'Participante adicionado!', icon: 'success' });
            } catch (err) {
                console.log(err);
                Swal({
                    title: 'Ocorreu um erro ao salvar o participante',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            } finally {
                loadingAddParticipant.value = false;
            }
        }

        async function copyRegistrationLink() {
            try {
                await navigator.clipboard.writeText(registrationLink.value);
                copied.value = true;
                setTimeout(() => { copied.value = false; }, 2000);
            } catch (err) {
                console.log(err);
                Swal({ title: 'Não foi possível copiar o link', icon: 'warning' });
            }
        }

        function relativeTime(value) {
            if (!value) return '';
            try {
                return formatDistanceToNow(new Date(value), { addSuffix: true, locale: ptBR });
            } catch (e) {
                return '';
            }
        }

        function triggerBlobDownload(response, fallbackName) {
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            let filename = fallbackName;
            const cd = response.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition']);
            if (cd) {
                const match = /filename="?([^"]+)"?/.exec(cd);
                if (match) filename = match[1];
            }
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }

        async function downloadCert(participant) {
            try {
                certLoading.value[participant.uuid] = true;
                const response = await EventService.downloadCertificate(route.params.uuid, participant.uuid);
                triggerBlobDownload(response, `certificado-${participant.name || 'participante'}.pdf`);
            } catch (err) {
                console.log(err);
                Swal({ title: 'Erro ao baixar o certificado', text: 'Tente novamente', icon: 'error' });
            } finally {
                certLoading.value[participant.uuid] = false;
            }
        }

        async function restoreEvent() {
            try {
                restoringEvent.value = true;
                await EventService.restore(route.params.uuid);
                toast('Evento restaurado!', { type: 'success', autoClose: 2500 });
                await loadEvent(route.params.uuid);
            } catch (err) {
                console.log(err);
                Swal({ title: 'Erro ao restaurar o evento', text: 'Tente novamente', icon: 'error' });
            } finally {
                restoringEvent.value = false;
            }
        }

        async function deleteParticipant(participant) {
            const confirmed = await Swal({
                title: 'Excluir participante?',
                text: `${participant.name} será removido do evento.`,
                icon: 'warning',
                buttons: ['Cancelar', 'Excluir'],
                dangerMode: true
            });

            if (!confirmed) return;

            try {
                deletingParticipant.value[participant.uuid] = true;
                await EventService.deleteParticipant(route.params.uuid, participant.uuid);
                toast('Participante excluído!', { type: 'success', autoClose: 2500 });
                await Promise.all([loadParticipants(), loadEvent(route.params.uuid)]);
            } catch (err) {
                console.log(err);
                deletingParticipant.value[participant.uuid] = false;
                Swal({ title: 'Erro ao excluir participante', text: 'Tente novamente', icon: 'error' });
            }
        }

        async function resendCert(participant) {
            try {
                resendLoading.value[participant.uuid] = true;
                await EventService.sendParticipantCertificate(route.params.uuid, participant.uuid);
                toast('Certificado reenviado!', { type: 'success', autoClose: 2500 });
            } catch (err) {
                console.log(err);
                Swal({ title: 'Erro ao reenviar o certificado', text: 'Tente novamente', icon: 'error' });
            } finally {
                resendLoading.value[participant.uuid] = false;
            }
        }

        async function downloadInsightsPdf() {
            try {
                loadingInsights.value = true;
                const response = await EventService.downloadInsights(route.params.uuid, insightsCompanyFilter.value.trim() || null);
                triggerBlobDownload(response, 'insights-evento.pdf');
            } catch (err) {
                console.log(err);
                Swal({ title: 'Erro ao gerar o PDF de insights', text: 'Tente novamente', icon: 'error' });
            } finally {
                loadingInsights.value = false;
            }
        }

        async function viewInsights() {
            try {
                loadingViewInsights.value = true;
                const { data } = await EventService.getInsightsData(route.params.uuid);
                insightsData.value = data;
                insightsCompanyFilter.value = '';
                insightsModalRef.value.setModalStatus('open');
            } catch (err) {
                console.log(err);
                Swal({ title: 'Erro ao carregar o resumo', text: 'Tente novamente', icon: 'error' });
            } finally {
                loadingViewInsights.value = false;
            }
        }

        function closeInsightsModal() {
            insightsModalRef.value.setModalStatus('close');
        }

        init();

        return {
            isEdit,
            loading,
            loadingSubmit,
            event,
            trainingTypeOptions,
            certificateTemplateOptions,
            formOptions,
            participants,
            participantsTotal,
            participantLimit,
            participantSearch,
            loadingParticipants,
            participantModalRef,
            insightsModalRef,
            insightsData,
            insightsCompanyFilter,
            filteredParticipantRows,
            filteredQuestions,
            loadingViewInsights,
            viewInsights,
            closeInsightsModal,
            isParticipantEdit,
            participantForm,
            loadingAddParticipant,
            copied,
            editing,
            loadingInsights,
            certLoading,
            resendLoading,
            restoringEvent,
            restoreEvent,
            deletingParticipant,
            deleteParticipant,
            registrationLink,
            startEdit,
            cancelEdit,
            onSelectTrainingType,
            submit,
            loadParticipants,
            onParticipantSearchTyping,
            changeParticipantPage,
            openAddParticipant,
            openEditParticipant,
            closeAddParticipant,
            submitParticipant,
            copyRegistrationLink,
            relativeTime,
            downloadCert,
            resendCert,
            downloadInsightsPdf,
            dateTime
        };
    }
}
</script>
